import { useEffect, useRef, useState } from "react";
import { http, fmtIDR } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { HandCoins, Check, X, Pencil, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { TransferCorrectDialog, cancelTransferById } from "./TransferCorrectDialog.jsx";
import { showOsNotification } from "../lib/notify";

const POLL_MS = 12000;

// Play a two-tone notification chime via Web Audio API — no asset file needed.
// Called on new pending request(s). Falls back silently if AudioContext unavailable.
function playChime(urgent = false) {
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    const ctx = new AC();
    const now = ctx.currentTime;
    const notes = urgent
      ? [880, 1046, 880, 1046, 1318] // more insistent for urgent
      : [1046, 1318]; // pleasant ding-dong
    const noteDur = 0.15;
    notes.forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.setValueAtTime(freq, now + i * noteDur);
      gain.gain.setValueAtTime(0.0001, now + i * noteDur);
      gain.gain.exponentialRampToValueAtTime(0.35, now + i * noteDur + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + i * noteDur + noteDur - 0.02);
      osc.connect(gain).connect(ctx.destination);
      osc.start(now + i * noteDur);
      osc.stop(now + i * noteDur + noteDur);
    });
    // Auto close context to free resources
    setTimeout(() => { try { ctx.close(); } catch {} }, (notes.length * noteDur + 0.2) * 1000);
  } catch {}
}

export default function CapitalRequestBell({ onCount } = {}) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.role === "super_admin";
  const [items, setItems] = useState([]);
  const [pending, setPending] = useState(0);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(null); // request id being processed
  const [approveDialog, setApproveDialog] = useState(null);
  const [approveAmount, setApproveAmount] = useState("");
  const [approveNote, setApproveNote] = useState("");
  const [approved, setApproved] = useState([]);
  const [correctId, setCorrectId] = useState(null);
  const isSuper = user?.role === "super_admin";
  const knownIdsRef = useRef(new Set());
  const initRef = useRef(false);

  useEffect(() => { onCount?.(pending); }, [pending]);

  const poll = async () => {
    if (!isAdmin) return;
    try {
      const { data } = await http.get("/capital-requests", { params: { scope: "hq", status: "pending", limit: 50 } });
      const rows = data.items || [];
      const known = knownIdsRef.current;
      let newCount = 0;
      let urgentNew = false;
      rows.forEach((r) => {
        if (!known.has(r.id)) {
          newCount += 1;
          if (r.urgent) urgentNew = true;
          known.add(r.id);
        }
      });
      // Only notify on subsequent polls (skip the first snapshot)
      if (initRef.current && newCount > 0) {
        playChime(urgentNew);
        const branchName = rows[0]?.from_branch_name;
        toast(`${newCount} permintaan modal baru dari cabang`, {
          description: branchName ? `Dari ${branchName}` : undefined,
          duration: 8000,
        });
        showOsNotification(
          `${newCount} permintaan modal baru`,
          {
            body: branchName ? `Dari cabang ${branchName}${urgentNew ? " · URGENT" : ""}` : "Dari cabang",
            tag: "capital-request",
            url: "/admin/dashboard",
          },
        );
      }
      initRef.current = true;
      setItems(rows);
      setPending(data.pending || 0);
      if (isSuper) {
        try {
          const { data: ad } = await http.get("/capital-requests", { params: { scope: "hq", status: "approved", limit: 20 } });
          setApproved((ad.items || []).filter((r) => r.transfer_id));
        } catch {}
      }
    } catch {}
  };

  useEffect(() => {
    if (!isAdmin) return;
    poll();
    const t = setInterval(poll, POLL_MS);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [isAdmin]);

  const approve = async () => {
    if (!approveDialog) return;
    setBusy(approveDialog.id);
    try {
      await http.post(`/capital-requests/${approveDialog.id}/approve`, {
        amount: approveAmount ? parseFloat(approveAmount) : undefined,
        note: approveNote || undefined,
      });
      toast.success(`Permintaan disetujui — transfer otomatis ke ${approveDialog.from_branch_name}`);
      setApproveDialog(null); setApproveAmount(""); setApproveNote("");
      poll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal setujui");
    } finally { setBusy(null); }
  };

  const reject = async (r) => {
    const note = prompt("Alasan penolakan (opsional):", "");
    if (note === null) return;
    setBusy(r.id);
    try {
      await http.post(`/capital-requests/${r.id}/reject`, { note });
      toast.success("Permintaan ditolak");
      poll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal");
    } finally { setBusy(null); }
  };

  if (!isAdmin) return null;

  return (
    <>
      <button onClick={() => setOpen(true)} data-testid="capital-bell"
        className="relative inline-flex items-center justify-center w-9 h-9 rounded-md border border-slate-200 bg-white/60 text-slate-600 hover:bg-slate-100 transition"
        title={pending > 0 ? `${pending} permintaan modal pending` : "Tidak ada permintaan modal"}>
        <HandCoins size={16} className={pending > 0 ? "text-amber-600" : ""} />
        {pending > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full bg-rose-600 text-white text-[10px] font-bold flex items-center justify-center">
            {pending}
          </span>
        )}
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl" data-testid="capital-inbox">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <HandCoins size={18} className="text-amber-500" />
              Kotak Masuk Permintaan Modal Cabang
              {pending > 0 && <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-semibold">{pending} pending</span>}
            </DialogTitle>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto space-y-2">
            {items.length === 0 && (
              <div className="p-10 text-center text-slate-400 text-sm">
                Tidak ada permintaan pending dari cabang.
              </div>
            )}
            {items.map((r) => (
              <div key={r.id} className={`p-3 rounded-md border ${r.urgent ? "border-rose-300 bg-rose-50" : "border-slate-200 bg-white"}`} data-testid={`capreq-${r.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-sm">{r.from_branch_name}</span>
                      {r.urgent && <span className="text-[10px] px-1.5 py-0.5 rounded bg-rose-600 text-white font-bold uppercase animate-pulse">URGENT</span>}
                      <span className="text-[10px] text-slate-500">{r.requested_by_name}</span>
                    </div>
                    <div className="font-mono-num text-xl font-bold text-slate-900 mt-1">{fmtIDR(r.amount_idr)}</div>
                    {r.note && <div className="text-xs text-slate-600 mt-1 italic">"{r.note}"</div>}
                    <div className="text-[10px] text-slate-400 mt-1 font-mono-num">{new Date(r.created_at).toLocaleString("id-ID", { timeZone: "Asia/Jakarta" })}</div>
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <Button size="sm" variant="outline" disabled={busy === r.id}
                      onClick={() => { setApproveDialog(r); setApproveAmount(String(r.amount_idr)); setApproveNote(""); }}
                      className="border-emerald-300 hover:bg-emerald-50 hover:text-emerald-700"
                      data-testid={`approve-${r.id}`}>
                      <Check size={13} className="mr-1" /> Setujui
                    </Button>
                    <Button size="sm" variant="outline" disabled={busy === r.id}
                      onClick={() => reject(r)}
                      className="border-rose-300 hover:bg-rose-50 hover:text-rose-700"
                      data-testid={`reject-${r.id}`}>
                      <X size={13} className="mr-1" /> Tolak
                    </Button>
                  </div>
                </div>
              </div>
            ))}
            {isSuper && approved.length > 0 && (
              <div className="pt-3 mt-1 border-t border-slate-200">
                <div className="text-xs font-semibold text-slate-600 mb-2">Sudah Disetujui — dapat Dikoreksi / Dibatalkan</div>
                <div className="space-y-2">
                  {approved.map((r) => (
                    <div key={r.id} className="p-3 rounded-md border border-emerald-200 bg-emerald-50/40" data-testid={`capreq-approved-${r.id}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="text-sm"><span className="font-semibold">{r.from_branch_name}</span> <span className="text-[10px] text-slate-500">{r.resolved_by_name ? `· disetujui ${r.resolved_by_name}` : ""}</span></div>
                          <div className="font-mono-num text-lg font-bold text-slate-900 mt-0.5">{fmtIDR(r.approved_amount || r.amount_idr)}</div>
                          <div className="text-[10px] text-slate-400 mt-0.5 font-mono-num">{r.resolved_at ? new Date(r.resolved_at).toLocaleString("id-ID", { timeZone: "Asia/Jakarta" }) : ""}</div>
                        </div>
                        <div className="flex flex-col gap-1.5">
                          <Button size="sm" variant="outline" className="border-slate-300" data-testid={`capreq-edit-${r.id}`}
                            onClick={() => setCorrectId(r.transfer_id)}>
                            <Pencil size={13} className="mr-1" /> Koreksi
                          </Button>
                          <Button size="sm" variant="outline" className="border-rose-300 text-rose-600 hover:bg-rose-50" data-testid={`capreq-cancel-${r.id}`}
                            onClick={() => cancelTransferById(r.transfer_id, `${r.from_branch_name} · ${fmtIDR(r.approved_amount || r.amount_idr)}`, poll)}>
                            <RotateCcw size={13} className="mr-1" /> Batal
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <TransferCorrectDialog transferId={correctId} open={!!correctId} onOpenChange={(o) => { if (!o) setCorrectId(null); }} onSaved={poll} />

      <Dialog open={!!approveDialog} onOpenChange={(v) => !v && setApproveDialog(null)}>
        <DialogContent data-testid="capital-approve-dialog">
          <DialogHeader><DialogTitle>Setujui Permintaan Modal</DialogTitle></DialogHeader>
          {approveDialog && (
            <div className="space-y-3">
              <div className="text-sm">
                <b>{approveDialog.from_branch_name}</b> meminta{" "}
                <b className="font-mono-num text-slate-900">{fmtIDR(approveDialog.amount_idr)}</b>
              </div>
              {approveDialog.note && <div className="text-xs text-slate-600 italic">"{approveDialog.note}"</div>}
              <div>
                <label className="text-xs">Nominal Disetujui (Rp)</label>
                <input type="number" step="0.01" value={approveAmount}
                  onChange={(e) => setApproveAmount(e.target.value)}
                  className="w-full h-11 px-3 rounded-md border border-slate-200 font-mono-num text-lg font-bold"
                  data-testid="approve-amount" />
                <div className="text-[10px] text-slate-500 mt-1">Bisa disetujui sebagian.</div>
              </div>
              <div>
                <label className="text-xs">Catatan (opsional)</label>
                <input type="text" value={approveNote} onChange={(e) => setApproveNote(e.target.value)}
                  className="w-full h-9 px-3 rounded-md border border-slate-200 text-sm"
                  data-testid="approve-note" placeholder="mis. akan dikirim setelah tutup shift" />
              </div>
              <div className="rounded bg-emerald-50 border border-emerald-200 p-2 text-xs text-emerald-800">
                Menyetujui akan otomatis: transfer Kas Rupiah dari Pusat ke <b>{approveDialog.from_branch_name}</b>,
                membuat jurnal Piutang/Utang Antar Cabang, dan menandai permintaan sebagai "approved".
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setApproveDialog(null)}>Batal</Button>
            <Button onClick={approve} disabled={busy === approveDialog?.id}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="btn-confirm-approve">
              <Check size={13} className="mr-1" /> Konfirmasi & Transfer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
